import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, ExternalLink, FileDown, RefreshCw } from 'lucide-react';
import { applyForget, deleteDocument, reparseDocument, updateDocument } from '@mkcore/knowledge/schema';
import { Badge, Button, buttonVariants, Card, ErrorState, Field, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { ConfirmButton } from '@/components/confirm';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { RelatedKnowledge } from '@/features/knowledge/panels';
import { documentQuery, provenanceQuery } from '@/features/knowledge/queries';
import { KnowledgeMarkdown, SourceIcon, SpaceLabel } from '@/features/knowledge/ui';

export const Route = createFileRoute('/_app/knowledge/documents/$id')({ component: DocumentPage });

const PAGE = 50_000;

/** A document taken in (CO-1107, CO-1148, CO-1152, CO-1143): its text, where it comes from, hide, boost, read again. */
function DocumentPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const canUpdate = useCan('knowledge:update');
  const canDelete = useCan('knowledge:delete');
  const [to, setTo] = useState(PAGE);
  const q = useQuery(documentQuery(id, { to }));
  const derived = useQuery(provenanceQuery('document', id));
  const update = useActionMutation(updateDocument, { success: t('knowledge.document.saved') });
  const reparse = useActionMutation(reparseDocument, { success: t('knowledge.document.reparsed') });
  const remove = useActionMutation(deleteDocument, { success: t('knowledge.document.deleted'), onSuccess: () => void navigate({ to: '/knowledge' }) });
  const forget = useActionMutation(applyForget, { success: t('knowledge.item.forgotten'), onSuccess: () => void navigate({ to: '/knowledge' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const d = q.data;
  const upload = d.source?.kind === 'upload';
  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/knowledge" search={{ space: d.spaceId, view: 'documents', ...(d.path && { path: d.path }) }} className="hover:underline">
            {d.space.name}
            {d.path && ` / ${d.path}`}
          </Link>
        }
        title={d.title}
        actions={
          <div className="flex flex-wrap gap-2">
            {d.url && (
              <a href={d.url} target="_blank" rel="noopener noreferrer" className={buttonVariants({ variant: 'secondary' })}>
                <ExternalLink /> {t('knowledge.document.original')}
              </a>
            )}
            {d.fileUrl && (
              <a href={d.fileUrl} className={buttonVariants({ variant: 'ghost' })}>
                <FileDown /> {t('knowledge.document.file')}
              </a>
            )}
            {canUpdate && d.source && (
              <Button variant="ghost" loading={reparse.isPending} onClick={() => reparse.mutate({ input: { id } })}>
                <RefreshCw /> {t('knowledge.document.reparse')}
              </Button>
            )}
            {canUpdate && (
              <Button variant="ghost" loading={update.isPending} onClick={() => update.mutate({ input: { id, hidden: !d.hidden }, ifMatch: d.version })}>
                {d.hidden ? <Eye /> : <EyeOff />} {t(d.hidden ? 'knowledge.document.show' : 'knowledge.document.hide')}
              </Button>
            )}
            {canDelete && upload && (
              <ConfirmButton label={t('common.delete')} title={t('knowledge.document.deleteTitle')} description={t('knowledge.document.deleteHint')} variant="danger" onConfirm={() => remove.mutateAsync({ input: { id }, ifMatch: d.version })} />
            )}
            {canDelete && !upload && (
              <ConfirmButton label={t('knowledge.item.forget')} title={t('knowledge.document.forgetTitle')} description={t('knowledge.document.forgetHint')} variant="danger" onConfirm={() => forget.mutateAsync({ input: { ids: [{ type: 'document', id }] } })} />
            )}
          </div>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {d.source && (
          <Badge tone="outline">
            <SourceIcon kind={d.source.kind} /> {d.source.name}
          </Badge>
        )}
        {d.status === 'failed' && <Badge tone="danger">{t('knowledge.document.failed')}</Badge>}
        {d.hidden && <Badge tone="warning">{t('knowledge.document.hidden')}</Badge>}
      </div>
      {d.error && (
        <p role="alert" className="mb-3 text-sm text-danger">
          {d.error}
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-3">
          <Card className="p-5">{d.text.trim() ? <KnowledgeMarkdown text={d.text} /> : <p className="text-sm text-fg-3">{t('knowledge.document.noText')}</p>}</Card>
          {d.length > to && (
            <div className="flex justify-center">
              <Button variant="secondary" onClick={() => setTo(to + PAGE)}>
                {t('common.more')}
              </Button>
            </div>
          )}
        </div>
        <div className="grid content-start gap-4">
          <Card role="region" aria-label={t('knowledge.item.details')} className="grid gap-3 p-4">
            <dl className="grid gap-1.5 text-sm">
              <Row label={t('knowledge.item.fields.space')}>
                <SpaceLabel space={d.space} />
              </Row>
              {d.author && <Row label={t('knowledge.document.author')}>{d.author}</Row>}
              <Row label={t('knowledge.document.type')}>{d.mimeType}</Row>
              <Row label={t('knowledge.document.size')}>{formatNumber(Math.ceil(d.size / 1024))} kB</Row>
              {d.updatedAtSource && <Row label={t('knowledge.document.changed')}>{formatDate(d.updatedAtSource.slice(0, 10))}</Row>}
              <Row label={t('knowledge.document.indexed')}>{formatDateTime(d.indexedAt)}</Row>
            </dl>
            {canUpdate && (
              <Field label={t('knowledge.document.boost')} hint={t('knowledge.document.boostHint')}>
                {(p) => (
                  <NativeSelect {...p} value={String(d.boost)} onChange={(e) => update.mutate({ input: { id, boost: Number(e.target.value) }, ifMatch: d.version })}>
                    {[-1, -0.5, 0, 0.5, 1].map((b) => (
                      <option key={b} value={String(b)}>
                        {t(`knowledge.document.boosts.${String(b).replace('-', 'm').replace('.', '_')}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
          </Card>
          {!!derived.data?.derived.length && (
            <Card role="region" aria-label={t('knowledge.provenance.title')} className="grid gap-2 p-4">
              <h2 className="text-sm font-bold">{t('knowledge.provenance.madeFrom')}</h2>
              <ul className="grid gap-1 text-sm">
                {derived.data.derived.map((x) => (
                  <li key={x.id}>
                    <span className="flex items-center gap-1.5">
                      <Badge tone="outline">{t(`knowledge.provenance.types.${x.type}`)}</Badge>
                      {x.type === 'item' ? (
                        <Link to="/knowledge/items/$id" params={{ id: x.id }} className="hover:underline">
                          {x.title ?? x.id}
                        </Link>
                      ) : (
                        <span>{x.title ?? t(`knowledge.provenance.types.${x.type}`)}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <RelatedKnowledge type="document" id={id} />
        </div>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
      <dt className="text-fg-3">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
